import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Divider, Container, Paper } from '@mui/material';
import { ArrowBack as BackIcon, Security as SecurityIcon } from '@mui/icons-material';

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';

const sections = [
  {
    title: '1. Purpose of Data Collection',
    body: `Eros Wellness collects and processes your personal and health data from you and from authorised healthcare or service locations where Eros Wellness is deployed or integrated, including government health facilities such as PHCs and Ayushman Arogya Mandirs, private hospitals and clinics, diagnostic centres, medical colleges, outreach camps, wearable or connected medical devices, mobile/web applications and patient portals for purposes including:`,
    bullets: [
      'Wellness screening, preventive health checks and health risk assessments',
      'Collection of vitals, measurements, device readings and lab/diagnostic results',
      'Generation of personalised wellness reports using medical devices, IoT tools and AI-assisted analytics',
      'Sending reports and follow-up communications through approved contact channels',
      'Meeting legal, regulatory, audit and public health requirements',
      'Enabling access to your health history through the Eros Wellness Patient Portal',
    ],
  },
  {
    title: '2. Data We Collect',
    subsections: [
      {
        label: 'Personal Identifiers',
        text: 'Name, age/date of birth, gender, contact number, email, patient ID, ABHA number (if provided), address/location details, guardian details.',
      },
      {
        label: 'Health & Screening Data',
        text: 'Blood pressure, heart rate, SpO2, glucose, haemoglobin, body composition, BMI, temperature, spirometry, questionnaire responses, health history and lifestyle inputs.',
      },
      {
        label: 'Biometric / AI Inputs',
        text: 'Face scan images/video frames, AI-inferred wellness indicators and risk scores, where applicable.',
      },
      {
        label: 'Technical & Consent Records',
        text: 'Device ID, application logs, user ID, IP address, device information, location of screening centre, date/time stamps, consent version and audit trails.',
      },
    ],
  },
  {
    title: '3. Disclosure and Sharing',
    body: `Your information may be shared only as necessary to provide healthcare services, comply with legal or public health obligations, or conduct approved research activities. Identifiable health data will only be shared with authorised healthcare providers, government/public health authorities, and service providers on a need-to-know basis. Identifiable health data will not be sold or used for direct marketing without your separate explicit consent.`,
  },
  {
    title: '4. Data Retention and Security',
    subsections: [
      {
        label: 'Data Retention',
        text: 'Your health records are retained for 3 years from the date of screening, unless you request earlier deletion.',
      },
      {
        label: 'Data Security',
        text: 'All data is encrypted using AES-256 encryption. Access is limited to authorised personnel only. Audit logs are maintained for all data access.',
      },
    ],
  },
  {
    title: '5. Your Rights (DPDP Act 2023)',
    rights: [
      { right: 'Access', desc: 'Request a copy of your stored data' },
      { right: 'Correction', desc: 'Correct inaccurate or incomplete data' },
      { right: 'Erasure', desc: 'Request deletion of data no longer required' },
      { right: 'Withdrawal', desc: 'Withdraw consent at any time' },
    ],
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f7fa', pb: 8 }}>
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${DARK_BLUE} 0%, #007BB5 100%)`, py: 5, px: 3 }}>
        <Container maxWidth="md">
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: 'rgba(255,255,255,0.8)', mb: 3, '&:hover': { color: '#fff' } }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '12px',
              background: `linear-gradient(135deg, ${BLUE}, ${GREEN})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SecurityIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase' }}>
                Eros Wellness
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Montserrat, sans-serif' }}>
                Privacy Policy
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', mt: 1 }}>
            Last updated: September 2026 &nbsp;·&nbsp; Version 1.0 &nbsp;·&nbsp; Governed by the Digital Personal Data Protection Act, 2023 (India)
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 3, sm: 5 }, border: '1px solid #e2e8f0' }}>
          <Typography sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.8, mb: 4 }}>
            This Privacy Policy explains how Eros Universe ("Eros Wellness", "we", "our") collects, uses, stores and protects your personal and health data when you use our AI Clinic Platform — including the kiosk application, web portal, and patient-facing services.
          </Typography>

          {sections.map((sec, idx) => (
            <Box key={idx} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 1.5, fontFamily: 'Montserrat, sans-serif' }}>
                {sec.title}
              </Typography>

              {sec.body && (
                <Typography sx={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.8, mb: sec.bullets ? 1.5 : 0 }}>
                  {sec.body}
                </Typography>
              )}

              {sec.bullets && (
                <Box component="ul" sx={{ pl: 3, mt: 0.5 }}>
                  {sec.bullets.map((b, i) => (
                    <Typography component="li" key={i} sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.8, mb: 0.5 }}>
                      {b}
                    </Typography>
                  ))}
                </Box>
              )}

              {sec.subsections && sec.subsections.map((sub, i) => (
                <Box key={i} sx={{ mb: 1.5, pl: 2, borderLeft: `3px solid ${BLUE}` }}>
                  <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 0.3 }}>
                    {sub.label}
                  </Typography>
                  <Typography sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.7 }}>
                    {sub.text}
                  </Typography>
                </Box>
              ))}

              {sec.rights && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {sec.rights.map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{
                        mt: 0.3, width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${BLUE}, ${GREEN})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 800 }}>✦</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{r.right}</Typography>
                        <Typography sx={{ color: '#475569', fontSize: '0.88rem' }}>{r.desc}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {idx < sections.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          ))}

          {/* Contact */}
          <Box sx={{ mt: 3, p: 3, borderRadius: 2, bgcolor: '#f0f9ff', border: `1px solid ${BLUE}22` }}>
            <Typography sx={{ fontWeight: 700, color: DARK_BLUE, mb: 0.5 }}>Data Queries & Grievances</Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.9rem' }}>
              Email: <Box component="a" href="mailto:privacy@erosuniverse.com" sx={{ color: BLUE, textDecoration: 'none', fontWeight: 600 }}>privacy@erosuniverse.com</Box>
            </Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.88rem', mt: 1 }}>
              We will respond to all data-related requests within 30 days as required under the DPDP Act, 2023.
            </Typography>
          </Box>
        </Paper>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94a3b8', mt: 3 }}>
          Eros Wellness Platform © {new Date().getFullYear()} Eros Universe · All rights reserved
        </Typography>
      </Container>
    </Box>
  );
}
